import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ChevronRight, ShoppingCart, Minus, Plus, Heart, Sparkles, RotateCcw, ShieldCheck } from 'lucide-react'
import { toast } from 'sonner'
import type { Product, Variant } from '../../types'
import { cleanBrDescription, extractTitleFromDescription, formatPrice } from '../../lib/utils'
import { useCart } from '../../context/CartContext'
import { useWishlist } from '../../context/WishlistContext'
import { useLocalePath } from '../layout/LanguageRoute'
import { Button } from '../ui/button'
import { localizeSpecKey } from './ProductAttributes'
import StockBadge from './StockBadge'
import DeliveryBadge from './DeliveryBadge'
import LifecycleBadge from './LifecycleBadge'
import PriceDisplay from './PriceDisplay'
import ProductImageGallery from './ProductImageGallery'

interface ProductDetailHeroProps {
  product: Product
  /** When provided, the hero shows variant-specific price/stock/image instead of the product's */
  variant?: Variant
  /** All variants of this product — needed to compute the price delta vs. the default variant. */
  variants?: Variant[]
}

export default function ProductDetailHero({ product, variant, variants }: ProductDetailHeroProps) {
  const { addItem } = useCart()
  const { isInWishlist, addItem: addToWishlist, removeItem: removeFromWishlist, items: wishlistItems } = useWishlist()
  const lp = useLocalePath()
  const { t } = useTranslation()
  const moq = product.moq || 1
  const [quantity, setQuantity] = useState(moq)

  // Use variant data when available, fall back to product data. The backend
  // omits null price fields from its JSON response (API Platform strips
  // nulls), so treat missing and explicit null as the same "no price" state.
  const inWishlist = isInWishlist(product.normalized_mpn)

  function handleToggleWishlist() {
    if (inWishlist) {
      const entry = wishlistItems.find(i => i.normalizedMpn === product.normalized_mpn)
      if (entry) removeFromWishlist(entry.uuid)
    } else {
      addToWishlist(product.normalized_mpn)
    }
  }

  const effectivePrice = (variant ? variant.effective_price : product.effective_price) ?? null
  const effectivePriceIncVat = (variant ? variant.effective_price_inc_vat : product.effective_price_inc_vat) ?? null
  const stockStatus = variant ? variant.stock_status : product.stock_status
  const lifecyclePhase = variant ? variant.lifecycle_phase : product.lifecycle_phase
  const primaryImageUrl = (variant?.primary_image_url) || product.primary_image_url
  const displayMpn = variant ? variant.normalized_mpn : product.normalized_mpn
  const rawTitle = variant ? variant.display_title : product.display_title
  // When display_title equals the MPN (no proper title in catalog), fall back
  // to a SHORT extract of the description — never the whole spec dump, which
  // produces a multi-line H1 on B&R servo drives / motors. The full text
  // still appears in the Overview tab.
  const cleanedDesc = cleanBrDescription(product.short_description)
  const fallbackTitle = extractTitleFromDescription(cleanedDesc, 80) || displayMpn
  const displayTitle = (rawTitle && rawTitle !== displayMpn) ? rawTitle : fallbackTitle
  const hasPriceData = effectivePrice != null
  // Discontinued (EOL) hardware: kept findable for SEO but not orderable — show
  // a clear notice instead of price/buy/quote controls (WEB-426).
  const isDiscontinued = lifecyclePhase === 'obsolete'

  // Variant delta: when viewing a non-default variant, show the price change
  // vs. the default variant so the customer immediately understands the cost
  // of picking the variant (coated, longer cable, etc.). See [[variant-model]].
  const defaultVariant = variants?.find(v => v.is_default) ?? null
  const variantPriceDelta =
    variant && defaultVariant && variant.uuid !== defaultVariant.uuid &&
    variant.effective_price != null && defaultVariant.effective_price != null
      ? parseFloat(variant.effective_price) - parseFloat(defaultVariant.effective_price)
      : null

  function handleAddToCart() {
    addItem({
      normalizedMpn: displayMpn,
      slug: product.slug,
      displayTitle: displayTitle,
      effectivePrice: effectivePrice,
      effectivePriceIncVat: effectivePriceIncVat,
      primaryImageUrl: primaryImageUrl,
      moq,
      quantity,
    })
    toast.success(t('shop.product.addedToCart'), { description: `${quantity}× ${displayTitle}` })
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 py-8">
      {/* Image gallery */}
      <ProductImageGallery
        primaryImageUrl={primaryImageUrl}
        galleryImages={product.gallery_images ?? []}
        images={product.images ?? []}
        alt={displayTitle}
      />

      {/* Info */}
      <div className="flex flex-col gap-4">
        {/* Breadcrumb hint */}
        <div className="flex items-center gap-1 text-xs text-neutral-600">
          <span>{product.category}</span>
          {product.series && (
            <>
              <ChevronRight className="h-3 w-3" />
              <span>{product.series}</span>
            </>
          )}
        </div>

        {/* MPN */}
        <span className="font-mono text-sm text-neutral-600 tracking-widest" data-testid="product-mpn">
          {displayMpn}
        </span>
        {product.manufacturer_part_number && product.manufacturer_part_number !== displayMpn && (
          <span className="text-xs text-neutral-600">
            Fabrikantcode: <span className="font-mono">{product.manufacturer_part_number}</span>
          </span>
        )}

        {/* Title */}
        <h1 className="text-2xl font-bold text-neutral-900 leading-snug" data-testid="product-title">
          {displayTitle}
        </h1>

        {/* Variant delta — what makes this variant different from the default */}
        {variant && variant.variant_label && (
          <div
            className="inline-flex flex-wrap items-center gap-2 self-start rounded-md border border-primary/30 bg-primary/5 px-3 py-1.5"
            data-testid="variant-delta"
          >
            <Sparkles className="h-3.5 w-3.5 text-primary shrink-0" aria-hidden="true" />
            <span className="text-xs font-semibold uppercase tracking-wide text-primary">
              {variant.variant_type === 'coated' ? 'Conformal coated' : variant.variant_label}
            </span>
            {variantPriceDelta != null && variantPriceDelta !== 0 && (
              <span className="text-xs text-neutral-700">
                {variantPriceDelta > 0 ? '+' : '−'}
                {formatPrice(Math.abs(variantPriceDelta))} vs. standaard
              </span>
            )}
            {defaultVariant && variant.uuid !== defaultVariant.uuid && (
              <span className="text-xs text-neutral-500">
                · specs &amp; documenten zijn gelijk aan {defaultVariant.normalized_mpn}
              </span>
            )}
          </div>
        )}

        {/* Badges */}
        <div className="flex flex-wrap gap-2">
          <StockBadge status={stockStatus} />
          <DeliveryBadge speed={product.delivery_speed} />
          <LifecycleBadge phase={lifecyclePhase} />
        </div>

        {/* Key specs — above the fold before price */}
        {(Object.keys(product.filterable_attributes ?? {}).length > 0 || (product.technical_attributes ?? []).length > 0) && (
          <KeySpecsPanel
            filterable_attributes={product.filterable_attributes ?? {}}
            technical_attributes={product.technical_attributes ?? []}
          />
        )}

        {/* Discontinued (EOL) — replaces price/buy/quote controls, localized */}
        {isDiscontinued && (
          <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" role="status">
            <p className="text-sm font-semibold text-amber-800">{t('shop.product.discontinuedTitle')}</p>
            <p className="mt-1 text-xs text-amber-700">{t('shop.product.discontinuedBody')}</p>
          </div>
        )}

        {/* Price */}
        {!isDiscontinued && (
        <div className="py-4 border-y border-neutral-100">
          <PriceDisplay
            effective_price={effectivePrice}
            effective_price_inc_vat={effectivePriceIncVat}
            price_formatted={(variant ? variant.price_formatted : product.price_formatted) ?? null}
            large
          />
          {moq > 1 && (
            <p className="text-xs text-neutral-600 mt-1">
              {t('shop.product.moq', { count: moq })}
            </p>
          )}
        </div>
        )}

        {/* Add to Cart / Offerte aanvragen */}
        {!isDiscontinued && (
        <div className="flex items-center gap-3">
          <div className="flex items-center border border-neutral-300 rounded-md">
            <button
              onClick={() => setQuantity(q => Math.max(moq, q - 1))}
              disabled={quantity <= moq}
              aria-label="Aantal verlagen"
              className="p-2 hover:bg-neutral-50 disabled:opacity-30"
            >
              <Minus className="h-4 w-4" />
            </button>
            <input
              type="number"
              value={quantity}
              onChange={e => setQuantity(Math.max(moq, parseInt(e.target.value) || moq))}
              min={moq}
              aria-label="Aantal"
              className="w-14 text-center text-sm border-x border-neutral-300 py-2 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
            />
            <button
              onClick={() => setQuantity(q => q + 1)}
              aria-label="Aantal verhogen"
              className="p-2 hover:bg-neutral-50"
            >
              <Plus className="h-4 w-4" />
            </button>
          </div>
          <Button variant="primary" size="md" onClick={handleAddToCart} className="flex-1">
            <ShoppingCart className="h-4 w-4" />
            {t('shop.product.addToCart')}
          </Button>
          <Button
            variant="secondary"
            size="md"
            onClick={handleToggleWishlist}
            aria-label={inWishlist ? 'Verwijder uit verlanglijst' : 'Voeg toe aan verlanglijst'}
            className={inWishlist ? 'border-red-200 bg-red-50 text-red-500 hover:bg-red-100' : ''}
          >
            <Heart className={`h-4 w-4 ${inWishlist ? 'fill-current' : ''}`} />
          </Button>
        </div>
        )}

        {!isDiscontinued && !hasPriceData && (
          <div className="flex flex-col gap-2">
            <p className="text-xs text-neutral-600">
              Prijs op aanvraag — na toevoeging nemen wij contact op over de prijs.
            </p>
            <Button variant="secondary" size="sm" asChild className="self-start">
              <Link to={lp(`/product/${product.slug}/offerte`)}>
                {t('shop.product.requestQuote')}
              </Link>
            </Button>
          </div>
        )}

        {/* Trust strip — return policy + warranty visible at point of sale */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-600">
          <Link to={lp('/cms/herroepingsrecht')} className="inline-flex items-center gap-1 hover:text-primary">
            <RotateCcw className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
            <span>14 dagen herroepingsrecht</span>
          </Link>
          <Link to={lp('/cms/garantie')} className="inline-flex items-center gap-1 hover:text-primary">
            <ShieldCheck className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
            <span>12 mnd garantie</span>
          </Link>
        </div>

      </div>
    </div>
  )
}

const PRIORITY_SPEC_KEYS = [
  // Voltage / current (cross-category)
  'nominal_voltage', 'supply_voltage', 'input_voltage', 'output_voltage',
  'nominal_current', 'output_current_a', 'max_voltage',
  // I/O counts
  'digital_inputs', 'digital_outputs', 'analog_inputs', 'analog_outputs', 'number_of_channels',
  // CPU / controller
  'cpu_type', 'clock_speed_mhz', 'ram_mb', 'flash_mb',
  // Physical / connector
  'cable_length', 'cable_type', 'number_of_pins', 'connector_type',
  // HMI / display
  'display_size', 'resolution',
  // Power
  'power_output_w',
  // Fieldbus
  'fieldbus_interfaces', 'communication_interfaces',
]

const MAX_SPECS = 6

interface KeySpecsPanelProps {
  filterable_attributes: Record<string, string>
  technical_attributes?: Array<{ key: string; value: string; unit?: string }>
}

function KeySpecsPanel({ filterable_attributes, technical_attributes = [] }: KeySpecsPanelProps) {
  const { i18n } = useTranslation()
  const lang = i18n.language
  const picked: [string, string][] = []

  for (const key of PRIORITY_SPEC_KEYS) {
    if (filterable_attributes[key]) {
      picked.push([key, filterable_attributes[key]])
      if (picked.length === MAX_SPECS) break
    }
  }

  if (picked.length < MAX_SPECS) {
    for (const [key, val] of Object.entries(filterable_attributes)) {
      if (!key.includes('.') && !picked.some(([k]) => k === key) && val) {
        picked.push([key, val])
        if (picked.length === MAX_SPECS) break
      }
    }
  }

  // Fall back to technical_attributes when filterable_attributes is sparse
  if (picked.length < 3) {
    for (const attr of technical_attributes) {
      const label = attr.key
      const val = attr.unit ? `${attr.value} ${attr.unit}` : attr.value
      if (val && !picked.some(([k]) => k === label)) {
        picked.push([label, val])
        if (picked.length === MAX_SPECS) break
      }
    }
  }

  if (picked.length === 0) return null

  return (
    <div className="grid grid-cols-2 gap-2" data-testid="key-specs-panel">
      {picked.map(([key, val]) => (
        <div key={key} className="bg-neutral-50 rounded-md px-3 py-2">
          <p className="text-[10px] text-neutral-600 uppercase tracking-wide leading-tight">{localizeSpecKey(key, lang)}</p>
          <p className="text-xs font-medium text-neutral-800 mt-0.5 truncate" title={val}>{val}</p>
        </div>
      ))}
    </div>
  )
}
